import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { LayoutDashboard, MessageSquare, Bell, Info, Settings, X, Menu } from "lucide-react";
import {
  selectSidebarPinned,
  selectSidebarMobileOpen,
  closeSidebarMobile,
  toggleSidebarPin,
} from "../../utils/appSlice";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { useScrollLock } from "../../hooks/useScrollLock";
import NavItem from "./NavItem";
import BEAGLELOGO from "../../static/beagle-logo.svg";
import { isUserAdmin } from "../../utils/authUtils";
import { SIDEBAR_CONFIG } from "../../utils/constants";

const COLLAPSE_WIDTH = 68;
const EXPAND_WIDTH = 240;
const HOVER_DELAY = 300;

const Sidebar = () => {
  const dispatch = useDispatch();
  const pinned = useSelector(selectSidebarPinned);
  const mobileOpen = useSelector(selectSidebarMobileOpen);
  const isMobile = useIsMobile();

  const [hovered, setHovered] = useState(false);
  const hoverTimerRef = useRef(null);
  const sidebarRef = useRef(null);

  const isAdmin = isUserAdmin();

  useScrollLock(isMobile && mobileOpen);

  const expanded = isMobile ? mobileOpen : (pinned || hovered);
  const sidebarWidth = expanded ? EXPAND_WIDTH : COLLAPSE_WIDTH;

  const handleMouseEnter = useCallback(() => {
    if (isMobile || pinned) return;
    hoverTimerRef.current = setTimeout(() => setHovered(true), HOVER_DELAY);
  }, [isMobile, pinned]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHovered(false);
  }, []);

  const handleNavClick = useCallback(() => {
    if (isMobile) dispatch(closeSidebarMobile());
  }, [isMobile, dispatch]);

  // Escape key to close mobile drawer
  useEffect(() => {
    if (!isMobile || !mobileOpen) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") dispatch(closeSidebarMobile());
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isMobile, mobileOpen, dispatch]);

  // Focus trap for mobile drawer
  useEffect(() => {
    if (!isMobile || !mobileOpen) return;

    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    // Move focus into drawer
    const firstFocusable = sidebar.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      const focusables = sidebar.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isMobile, mobileOpen]);

  // Cleanup hover timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  const navItems = useMemo(() => [
    { to: "/beagle-chat", icon: LayoutDashboard, label: "Insights" },
    { to: "/beagle-chat/chat", icon: MessageSquare, label: "Chat" },
    ...(SIDEBAR_CONFIG.showNotification ? [{ to: "/beagle-chat/notification", icon: Bell, label: "Notification" }] : []),
    { to: "/beagle-chat/about", icon: Info, label: "About" },
  ], []);

  return (
    <>
      {/* Backdrop — always mounted for animation, toggled via opacity */}
      {isMobile && (
        <div
          className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300
            ${mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={() => dispatch(closeSidebarMobile())}
          aria-hidden="true"
        />
      )}

      <aside
        ref={sidebarRef}
        className={`
          fixed inset-y-0 left-0 z-50 flex flex-col
          bg-sidebar border-r border-white/5
          transition-all duration-300 ease-in-out
          ${isMobile && !mobileOpen ? '-translate-x-full' : 'translate-x-0'}
          ${!isMobile && hovered && !pinned ? 'shadow-2xl' : ''}
        `}
        style={{ width: isMobile ? EXPAND_WIDTH : sidebarWidth }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role={isMobile && mobileOpen ? "dialog" : "navigation"}
        aria-label={isMobile && mobileOpen ? "Navigation drawer" : "Main navigation"}
        aria-modal={isMobile && mobileOpen ? "true" : undefined}
      >
        <div className="h-[52px] flex items-center px-4 border-b border-white/5 shrink-0">
          {isMobile ? (
            <>
              <img
                src={BEAGLELOGO}
                alt="BeagleGPT"
                className="w-8 h-8 shrink-0 cursor-pointer"
                onClick={() => window.location.reload()}
              />
              <span className="ml-3 text-white font-semibold text-base whitespace-nowrap overflow-hidden">
                BeagleGPT
              </span>
              <button
                onClick={() => dispatch(closeSidebarMobile())}
                className="ml-auto text-slate-400 hover:text-white transition-colors p-1"
                aria-label="Close sidebar"
              >
                <X size={20} />
              </button>
            </>
          ) : (
            <button
              onClick={() => dispatch(toggleSidebarPin())}
              className="p-2 rounded-lg transition-colors text-slate-400 hover:text-white"
              aria-label="Toggle sidebar"
              aria-expanded={pinned}
            >
              <Menu size={20} />
            </button>
          )}
        </div>

        <nav className="flex-1 py-4 space-y-1 overflow-y-auto hide-scrollbar">
          {navItems.map((item) => (
            <NavItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
              expanded={expanded}
              onClick={handleNavClick}
            />
          ))}
        </nav>

        <div className="border-t border-white/5 py-3 space-y-1 shrink-0">
          {isAdmin && (
            <NavItem
              to="/beagle-chat/admin"
              icon={Settings}
              label="Admin"
              expanded={expanded}
              onClick={handleNavClick}
            />
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
